import { GroupFoodDrink, GroupPackaging } from '@shared/api/types';
import Text from '@shared/components/content/text';
import Unit from '@shared/components/content/unit';
import { ActivityCell } from '@shared/components/data-table/cells/activity-cell';
import { HeaderCell } from '@shared/components/data-table/cells/header-cell';
import { NumberCell } from '@shared/components/data-table/cells/number-cell';
import { ColumnDef } from '@tanstack/react-table';

/**
 * Shared column helpers for the group purchased-goods tables. Food and
 * packaging share the leading (Item → EF) and trailing (Quantity → Emissions)
 * columns; each table inserts its own kind-specific Rating column in between.
 */
export type PurchasedGoodsBaseRow = GroupFoodDrink | GroupPackaging;

export function getLeadingColumns<
  TRow extends PurchasedGoodsBaseRow,
>(): ColumnDef<TRow>[] {
  return [
    {
      id: 'name',
      accessorKey: 'name',
      size: 280,
      minSize: 220,
      header: ({ column }) => <HeaderCell column={column} name="Item" />,
      cell: ({ getValue, row }) => (
        <ActivityCell
          name={getValue<string>()}
          remoteId={row.original.externalId}
        />
      ),
      enableColumnFilter: true,
      meta: {
        label: 'Items',
        variant: 'text',
        placeholder: 'Search items…',
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
    {
      id: 'suppliers',
      accessorKey: 'suppliers',
      size: 200,
      header: ({ column }) => <HeaderCell column={column} name="Suppliers" />,
      cell: ({ getValue }) => {
        const suppliers = (getValue<readonly unknown[]>() as string[]) || [];
        if (suppliers.length === 0) return <Text variant="subtle">-</Text>;
        return (
          <Text
            includeHover
            className="line-clamp-2 truncate max-w-[200px]"
            variant="subtle"
          >
            {suppliers.join(', ')}
          </Text>
        );
      },
      enableSorting: false,
    },
    {
      id: 'numOrgs',
      accessorKey: 'numOrgs',
      size: 70,
      header: ({ column }) => (
        <HeaderCell column={column} name="Orgs" numberColumn />
      ),
      cell: ({ getValue }) => (
        <NumberCell number={getValue<number>()} rounded />
      ),
    },
    {
      id: 'emissionFactor',
      accessorKey: 'emissionFactor',
      size: 120,
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Emission factor"
          numberColumn
          unit={<Unit variant="kgCO2e/kg" />}
        />
      ),
      cell: ({ getValue }) => {
        const value = getValue<number | null>();
        if (value === null)
          return <p className="pr-[18px] text-right text-gray-500">-</p>;
        return <NumberCell number={value} />;
      },
    },
  ];
}

export function getTrailingColumns<
  TRow extends PurchasedGoodsBaseRow,
>(): ColumnDef<TRow>[] {
  return [
    {
      id: 'totalQuantityKg',
      accessorKey: 'totalQuantityKg',
      size: 100,
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Quantity"
          numberColumn
          unit={<Unit variant="tonnes" />}
        />
      ),
      cell: ({ getValue }) => <NumberCell number={getValue<number>() / 1000} />,
    },
    {
      id: 'totalEmissionsTonnes',
      accessorKey: 'totalEmissionsTonnes',
      size: 120,
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Emissions"
          numberColumn
          unit={<Unit variant="tCO2e" />}
        />
      ),
      cell: ({ getValue }) => <NumberCell number={getValue<number>()} />,
    },
  ];
}

export type FilterItem = { name: string; value: string };
export type FilterOption = { label: string; value: string };

export function toFilterOptions(
  entries: readonly FilterItem[] | undefined
): FilterOption[] {
  if (!entries) return [];
  return entries.map((entry) => ({ label: entry.name, value: entry.value }));
}

export function splitCsv(value: string | null): string[] | undefined {
  if (!value) return undefined;
  return value.split(',').filter(Boolean);
}
